import { useState, type FormEvent } from 'react';
import type { ContactListItem, Lead } from '@neuroedge/shared';
import { Button, Modal, TextField, useToast } from '../../components/ui';
import { ApiClientError } from '../../api/client';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import { useAuth } from '../../auth/AuthContext';
import { AssigneeSelect } from '../../components/assignee/AssigneeSelect';
import { useContacts } from '../../api/contacts';
import { useLeads } from '../../api/leads';
import { useCreateFollowUp } from '../../api/follow-ups';

/** `datetime-local` value (local time, no seconds) → ISO string. */
function toIso(local: string): string {
  return new Date(local).toISOString();
}

/** A Date as the value of an <input type="datetime-local"> (local time). */
function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * A sensible default due time on `day`: 10:00, or - when that has already
 * passed (e.g. today) - the next whole hour.
 */
function defaultDue(day: Date): string {
  const d = new Date(day);
  d.setHours(10, 0, 0, 0);
  if (d.getTime() <= Date.now()) {
    const next = new Date();
    next.setHours(next.getHours() + 1, 0, 0, 0);
    return toLocalInput(next);
  }
  return toLocalInput(d);
}

type Target = 'lead' | 'contact';

const pickButtonStyle = {
  width: '100%',
  textAlign: 'left',
  background: 'none',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--space-2)',
  cursor: 'pointer',
} as const;

/**
 * Quick "Add follow-up" (Dashboard quick action and the Follow-ups page):
 * pick a lead (searched by title or customer name/phone) or a contact, then
 * the usual title/due/assign fields - the same `useCreateFollowUp` call the
 * inline form on a lead or contact page makes. A lead's follow-up is also
 * attached to the lead's contact, so it shows on both pages.
 */
export function AddFollowUpQuickModal({
  onClose,
  initialDay,
}: {
  onClose: () => void;
  /** Pre-fills the due date (the day picked on the Follow-ups calendar). */
  initialDay?: Date;
}) {
  const { tenantUserId, hasPermission } = useAuth();
  const toast = useToast();
  const canLeads = hasPermission('leads.read');

  /** Until someone switches, a lead when they can see leads (permissions may resolve late). */
  const [chosenTarget, setTarget] = useState<Target | null>(null);
  const target: Target = canLeads ? (chosenTarget ?? 'lead') : 'contact';
  const [search, setSearch] = useState('');
  const term = search.trim();
  const [contact, setContact] = useState<ContactListItem | null>(null);
  const [lead, setLead] = useState<Lead | null>(null);
  const contacts = useContacts({ search: term || undefined, pageSize: 10 });
  // Lead search matches the title; "contact" matches the customer's name or phone.
  const leadsByTitle = useLeads({ search: term, pageSize: 10 }, target === 'lead' && !!term);
  const leadsByCustomer = useLeads({ contact: term, pageSize: 10 }, target === 'lead' && !!term);
  const leadMatches = [...(leadsByTitle.data?.items ?? []), ...(leadsByCustomer.data?.items ?? [])]
    .filter((l, i, all) => all.findIndex((x) => x.id === l.id) === i)
    .slice(0, 10);
  const leadsLoaded = !!leadsByTitle.data && !!leadsByCustomer.data;

  const [title, setTitle] = useState('');
  const [due, setDue] = useState(() => (initialDay ? defaultDue(initialDay) : ''));
  const [assigneeId, setAssigneeId] = useState<string | null>(null);
  /** Until someone is picked, it's whoever is signed in (even if that resolves late). */
  const assignee = assigneeId ?? tenantUserId ?? '';
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const create = useCreateFollowUp();

  const clear = (key: string) =>
    setFieldErrors((f) => {
      if (!f[key]) return f;
      const { [key]: _removed, ...rest } = f;
      return rest;
    });

  const picked = target === 'lead' ? lead : contact;
  const targetKey = target === 'lead' ? 'leadId' : 'contactId';

  function validate(): Record<string, string> {
    const errors: Record<string, string> = {};
    if (!picked) errors[targetKey] = target === 'lead' ? 'Choose a lead.' : 'Choose a contact.';
    if (title.trim().length > 200) errors.title = 'Title must be 200 characters or fewer.';
    if (!due) errors.dueAt = 'Pick a date and time.';
    else if (Number.isNaN(new Date(due).getTime())) errors.dueAt = 'Enter a valid date and time.';
    return errors;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Please fix the highlighted fields.');
      return;
    }
    setError(null);
    try {
      await create.mutateAsync({
        ...(lead && target === 'lead'
          ? { leadId: lead.id, contactId: lead.contact.id }
          : { contactId: contact!.id }),
        dueAt: toIso(due),
        title: title.trim() || undefined,
        assignedUserId: assignee || undefined,
      });
      toast.success('Follow-up scheduled.');
      onClose();
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const serverFieldErrors = fieldErrorsFromApiError(err);
        if (Object.keys(serverFieldErrors).length > 0) {
          setFieldErrors(serverFieldErrors);
          setError('Please fix the highlighted fields.');
          return;
        }
      }
      const message = err instanceof Error ? err.message : 'Could not schedule the follow-up.';
      setError(message);
      toast.error(message);
    }
  }

  function switchTarget(next: Target) {
    setTarget(next);
    setSearch('');
    clear('leadId');
    clear('contactId');
  }

  const targetError = fieldErrors.leadId ?? fieldErrors.contactId;

  return (
    <Modal title="Add follow-up" onClose={onClose}>
      <form onSubmit={onSubmit} style={{ display: 'grid', gap: 'var(--space-3)' }} noValidate>
        {canLeads ? (
          <div
            role="group"
            aria-label="Follow up on"
            style={{ display: 'flex', gap: 'var(--space-2)' }}
          >
            <Button
              variant={target === 'lead' ? 'primary' : 'secondary'}
              aria-pressed={target === 'lead'}
              onClick={() => switchTarget('lead')}
            >
              Lead
            </Button>
            <Button
              variant={target === 'contact' ? 'primary' : 'secondary'}
              aria-pressed={target === 'contact'}
              onClick={() => switchTarget('contact')}
            >
              Contact
            </Button>
          </div>
        ) : null}

        {picked ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <span>
              {lead && target === 'lead' ? (
                <>
                  <strong>{lead.title}</strong> — {lead.contact.displayName}
                </>
              ) : (
                <>
                  <strong>{contact!.displayName}</strong>
                  {contact!.phone ? ` — ${contact!.phone}` : ''}
                </>
              )}
            </span>
            <Button
              variant="secondary"
              onClick={() => (target === 'lead' ? setLead(null) : setContact(null))}
            >
              Change
            </Button>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
            <TextField
              label={target === 'lead' ? 'Lead *' : 'Contact *'}
              placeholder={
                target === 'lead'
                  ? 'Search by lead title, customer name or phone'
                  : 'Search by name, phone, or email'
              }
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                clear(targetKey);
              }}
              error={targetError}
            />
            {term && target === 'lead' && leadsLoaded ? (
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 2 }}>
                {leadMatches.length === 0 ? (
                  <li style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                    No leads match.
                  </li>
                ) : (
                  leadMatches.map((l) => (
                    <li key={l.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setLead(l);
                          setSearch('');
                        }}
                        style={pickButtonStyle}
                      >
                        {l.title} — {l.contact.displayName}
                        {l.contact.phone ? ` (${l.contact.phone})` : ''}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            ) : null}
            {term && target === 'contact' && contacts.data ? (
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 2 }}>
                {contacts.data.items.length === 0 ? (
                  <li style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                    No contacts match.
                  </li>
                ) : (
                  contacts.data.items.map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setContact(c);
                          setSearch('');
                        }}
                        style={pickButtonStyle}
                      >
                        {c.displayName} {c.phone ? `— ${c.phone}` : ''}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            ) : null}
          </div>
        )}

        <TextField
          label="Follow up about"
          placeholder="Follow up about…"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            clear('title');
          }}
          error={fieldErrors.title}
        />
        <TextField
          label="Due *"
          type="datetime-local"
          value={due}
          onChange={(e) => {
            setDue(e.target.value);
            clear('dueAt');
          }}
          error={fieldErrors.dueAt}
        />
        <AssigneeSelect value={assignee} onChange={setAssigneeId} />

        {error ? <p style={{ margin: 0, color: 'var(--color-danger)' }}>{error}</p> : null}
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Scheduling…' : 'Schedule'}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
}
